import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components/macro';
import useQuery from 'hooks/useQuery';
import CustomLink from 'components/CustomLink';
import { useSelector } from 'react-redux';
import { APPLICANT, getApplicantStatusDisplay, statusKeyToDisplay, statusLookup } from 'utility/lookup';
import { getCombineRoute } from 'utility/route';
import { SHyperLink } from 'style/CommonStyledComponent';

const SLayout = styled.div`
    padding: 10px;
    display: flex;
    border-bottom: 1px solid #dcdcdc;
    background: #f8f8fd;
    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
`;

const SSeparator = styled.div`
    margin: 0 15px;
`;

const NavigationBar = () => {
    const location = useLocation();
    const [pathArray, setPathArray] = useState([]);
    const applicationDetail = useSelector((state) => state.applicationDetail);
    const id = useQuery('id');
    const applicationSubmissionNo = applicationDetail?.[id]?.applicationForm?.subNo;
    const applicationStatusId = applicationDetail?.[id]?.status?.statusId;
    const history = useHistory();
    const applicationFolder = useSelector((state) => state.application.folder || 'all');
    const postApprovalFolder = useSelector((state) => state.postApproval.folder || 'all');
    const userMenu = useSelector((state) => state.menu);
    const user = useSelector((state) => state.user);
    const progressReport = useSelector((state) => state.progressReport);
    const finalReport = useSelector((state) => state.finalReport);

    const reportId = useQuery('reportId');
    const global = useSelector((state) => state.global);
    const showNavigationSubNo = global.postApprovalReportData?.[reportId]?.showNavigationSubNo;
    const showNavigationStatus = global.postApprovalReportData?.[reportId]?.showNavigationStatus;

    useEffect(() => {
        const currentPath = location.pathname;

        if (currentPath === '/') {
            setPathArray([]);
            return;
        }

        const data = currentPath.split('/').filter((item) => item !== '');
        const isSubRoute = data.length > 1;

        const result = [];

        const combineRoute = getCombineRoute(userMenu, user.role);

        const route = combineRoute.find((item) => {
            return item.path === `/${data[0]}`;
        });

        const isApplicationDetail = currentPath === '/applications/initial-application-form';
        const isApplicationHistory = currentPath.startsWith('/applications/application-history');
        const isApplicationNew = currentPath.startsWith('/applications/new');
        const isApplicationClone = currentPath.startsWith('/applications/clone');
        const isApplicationAmendmentCreate = currentPath.startsWith('/applications/create-application-amendment');
        const isApplicationAmendmentDetail = currentPath.startsWith(
            '/post-approval-document/application-amendment-detail',
        );
        const isChangeDelegation = currentPath.startsWith('/applications/change-delegation');
        const isAppConfirmReviewResult = currentPath.startsWith('/applications/confirm-review-result');
        const isPostAppConfirmReviewResult = currentPath.startsWith('/post-approval-document/confirm-review-result');
        // /post-approval-document/confirm-review-result

        if (isPostAppConfirmReviewResult) {
            result.push({ name: `Post Approval Document`, url: `/post-approval-document/${postApprovalFolder}` });
            result.push({ name: 'Confirm Review Result' });
        } else if (isAppConfirmReviewResult) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: 'Confirm Review Result' });
        } else if (isChangeDelegation) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: `Change Delegation` });
        } else if (isApplicationAmendmentDetail) {
            const status = getApplicationStatusDisplay() ? ` (${getApplicationStatusDisplay()})` : '';
            result.push({ name: `Post Approval Document`, url: `/post-approval-document/${postApprovalFolder}` });
            result.push({
                name: `Application amendment${applicationSubmissionNo ? ` (${applicationSubmissionNo})` : ''}${status}`,
            });
        } else if (isApplicationAmendmentCreate) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: `Application amendment` });
        } else if (isApplicationClone) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: `Clone` });
        } else if (isApplicationNew) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: `Create` });
        } else if (isApplicationHistory) {
            result.push({ name: `Initial Application`, url: `/applications/${applicationFolder}` });
            result.push({ name: `Initial application overview` });
        } else if (isApplicationDetail) {
            result.push({ name: 'Initial Application', url: `/applications/${applicationFolder}` });
            result.push({ name: 'Initial Application', isApplicationDetail: true });
        } else if (!route) {
            const temp = combineRoute.find((item) => {
                return item.path?.toLowerCase() === currentPath.toLowerCase();
            });

            result.push({ name: temp?.name ?? 'undefined' });
        } else {
            if (showNavigationSubNo || showNavigationStatus) {
                result.push({
                    name: `
                        ${route.name}
                        ${showNavigationSubNo ? ` (${showNavigationSubNo})` : ''}
                        ${showNavigationStatus ? ` (${showNavigationStatus})` : ''}
                    `,
                });
            } else {
                result.push({ name: route.name });
            }

            if (isSubRoute) {
                result[0].url = route.url ?? '#';

                const subRoute = route.subRoute.find((item) => {
                    return item.path?.toLowerCase() === `/${data[1]?.toLowerCase()}`;
                });

                result.push({ name: subRoute?.name });
            }
        }

        setPathArray(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, userMenu, applicationDetail, global]);

    const getApplicationStatusDisplay = () => {
        return user.role === APPLICANT
            ? getApplicantStatusDisplay(applicationStatusId)
            : statusLookup.find((item) => item.key === applicationStatusId)?.statusDisplay;
    };

    const getDocumentStatus = (item) => {
        const url = location.pathname;
        // other form should specified here

        if (url.startsWith('/progress-report')) {
            if (progressReport?.remoteDetail?.subNo && !progressReport.isLoading) {
                return `(${progressReport.remoteDetail.subNo}) (${statusKeyToDisplay(
                    progressReport.remoteDetail.lastStatus,
                )})`;
            }
        } else if (url.startsWith('/final-report')) {
            if (finalReport?.remoteDetail?.subNo && !finalReport.isLoading) {
                return `(${finalReport.remoteDetail.subNo}) (${statusKeyToDisplay(
                    finalReport.remoteDetail.lastStatus,
                )})`;
            }
        } else {
            // case besides progress report and final report
            if (item.isApplicationDetail && applicationSubmissionNo) {
                return (
                    <div>
                        ({applicationSubmissionNo}) ({getApplicationStatusDisplay()})
                    </div>
                );
            }
        }

        return null;
    };

    return (
        <SLayout>
            <CustomLink to={'/'}>Home</CustomLink>
            {pathArray.map((item, index) => {
                const url = item.url;

                return (
                    <div style={{ display: 'flex' }} key={`${item.name}${item.url}`}>
                        <SSeparator>></SSeparator>
                        {url ? (
                            <SHyperLink
                                onClick={() => {
                                    history.push(item.url);
                                }}
                            >
                                {item.name}
                            </SHyperLink>
                        ) : (
                            <div>{item.name}</div>
                        )}

                        <div style={{ marginLeft: 5 }}>{getDocumentStatus(item)}</div>
                    </div>
                );
            })}
        </SLayout>
    );
};

export default NavigationBar;
